// Targets: every salesperson × every month of the financial year on one grid.
// Type into cells, fill a quarter / the year in one go, or import a CSV —
// nothing is saved until "Save" (with a reason, kept in the audit log). Only
// Super Admin / Admin / Department Administrator can change them.
import { useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Download, Save, Target, Upload, Wand2, X } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { errText, inr, teamApi, usePerfMutation, useTargetsGrid, type TargetCell } from '../api';
import { Dialog, Empty, Field, inputCls, Panel } from './ui';

type MetricKey = keyof TargetCell;
const METRICS: Array<{ k: MetricKey; label: string; money: boolean; csv: string }> = [
  { k: 'amount', label: 'Sales target (₹)', money: true, csv: 'sales_target' },
  { k: 'newSalesTarget', label: 'New-customer sales (₹)', money: true, csv: 'new_sales_target' },
  { k: 'newCustomerTarget', label: 'New customers', money: false, csv: 'new_customer_target' },
  { k: 'count', label: 'Invoices', money: false, csv: 'invoice_count_target' },
];
const keyOf = (u: string, m: string) => `${u}|${m}`;

/** Minimal CSV reader: commas, quotes, CRLF. */
function parseCsv(text: string): string[][] {
  const out: string[][] = []; let row: string[] = []; let cell = ''; let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i += 1; } else if (ch === '"') quoted = false; else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && text[i + 1] === '\n') i += 1; row.push(cell); out.push(row); row = []; cell = ''; }
    else cell += ch;
  }
  if (cell || row.length) { row.push(cell); out.push(row); }
  return out.filter((r) => r.some((c) => c.trim()));
}

export function TargetsTab({ fy }: { fy: string }) {
  const { data, isLoading } = useTargetsGrid(fy);
  const [metric, setMetric] = useState<MetricKey>('amount');
  const [draft, setDraft] = useState<Record<string, Partial<TargetCell>>>({});
  const [fill, setFill] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const save = usePerfMutation((v: { rows: Array<{ userId: string; periodKey: string } & Partial<TargetCell>>; reason: string }) => teamApi.saveTargets(v.rows, v.reason));
  const m = METRICS.find((x) => x.k === metric)!;

  const cellValue = (u: string, mk: string, k: MetricKey) => {
    const d = draft[keyOf(u, mk)];
    if (d && d[k] !== undefined) return d[k] as number;
    return data?.rows.find((r) => r.id === u)?.months[mk]?.[k] ?? 0;
  };
  const isDirty = (u: string, mk: string, k: MetricKey) => {
    const d = draft[keyOf(u, mk)];
    return !!d && d[k] !== undefined && d[k] !== (data?.rows.find((r) => r.id === u)?.months[mk]?.[k] ?? 0);
  };
  const changes = useMemo(() => {
    if (!data) return [];
    const list: Array<{ userId: string; periodKey: string } & Partial<TargetCell>> = [];
    for (const [key, d] of Object.entries(draft)) {
      const [userId, periodKey] = key.split('|');
      const orig = data.rows.find((r) => r.id === userId)?.months[periodKey];
      const diff: Partial<TargetCell> = {};
      (Object.keys(d) as MetricKey[]).forEach((k) => { if (d[k] !== undefined && d[k] !== (orig?.[k] ?? 0)) diff[k] = d[k]; });
      if (Object.keys(diff).length) list.push({ userId, periodKey, ...diff });
    }
    return list;
  }, [draft, data]);
  const put = (u: string, mk: string, k: MetricKey, v: number) => setDraft((p) => ({ ...p, [keyOf(u, mk)]: { ...p[keyOf(u, mk)], [k]: v } }));

  if (isLoading || !data) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (!data.rows.length) return <Empty title="No sales employees" text="Only people in the Sales or Field Executive department appear here." />;
  const ro = !data.canEdit;
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  const colTotal = (mk: string) => data.rows.reduce((n, r) => n + Number(cellValue(r.id, mk, metric) || 0), 0);
  const fmt = (v: number) => (m.money ? inr(v) : String(v));

  const downloadTemplate = () => {
    const head = ['employee_id', 'employee_name', 'month', ...METRICS.map((x) => x.csv)];
    const lines = [head.join(',')];
    for (const r of data.rows) for (const mo of data.months) lines.push([r.id, `"${r.name.replace(/"/g, '""')}"`, mo.key, ...METRICS.map((x) => cellValue(r.id, mo.key, x.k))].join(','));
    const url = URL.createObjectURL(new Blob([String.fromCharCode(0xfeff) + lines.join('\r\n')], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `sales-targets-FY${fy}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const importCsv = async (file: File) => {
    const rows = parseCsv((await file.text()).replace(new RegExp(`^${String.fromCharCode(0xfeff)}`), ''));
    if (rows.length < 2) { toast.error('The file has no rows.'); return; }
    const head = rows[0].map((h) => h.trim().toLowerCase());
    const col = (n: string) => head.indexOf(n);
    if (col('month') < 0 || (col('employee_id') < 0 && col('employee_name') < 0)) { toast.error('Use the template: it needs employee_id (or employee_name) and month columns.'); return; }
    const monthKeys = new Set(data.months.map((x) => x.key));
    const problems: string[] = []; let applied = 0;
    const next = { ...draft };
    rows.slice(1).forEach((r, i) => {
      const id = col('employee_id') >= 0 ? r[col('employee_id')]?.trim() : '';
      const name = col('employee_name') >= 0 ? r[col('employee_name')]?.trim().toLowerCase() : '';
      const emp = data.rows.find((e) => e.id === id) || data.rows.find((e) => name && e.name.toLowerCase() === name);
      const mk = r[col('month')]?.trim();
      if (!emp) { problems.push(`Line ${i + 2}: employee not found`); return; }
      if (!monthKeys.has(mk)) { problems.push(`Line ${i + 2}: month ${mk || '(blank)'} is not in FY ${fy}`); return; }
      const cell: Partial<TargetCell> = { ...next[keyOf(emp.id, mk)] };
      let bad = false;
      METRICS.forEach((x) => {
        const c = col(x.csv); if (c < 0) return;
        const raw = (r[c] || '').replace(/[₹,\s]/g, ''); if (raw === '') return;
        const n = Number(raw); if (!Number.isFinite(n) || n < 0) { bad = true; return; }
        cell[x.k] = n;
      });
      if (bad) { problems.push(`Line ${i + 2}: a target is not a number of zero or more`); return; }
      next[keyOf(emp.id, mk)] = cell; applied += 1;
    });
    setDraft(next);
    if (problems.length) toast.error(`${problems.length} line(s) skipped — ${problems.slice(0, 3).join('; ')}`, { duration: 8000 });
    toast.success(`${applied} line(s) loaded into the grid. Check them, then press Save.`);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200/80 bg-white p-3">
        <div className="flex rounded-lg bg-slate-100 p-0.5">
          {METRICS.map((x) => (
            <button key={x.k} type="button" onClick={() => setMetric(x.k)} className={`rounded-md px-3 py-1.5 text-[13px] font-semibold ${metric === x.k ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'}`}>{x.label}</button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {!ro && <button type="button" onClick={() => setFill(true)} className="btn-sp"><Wand2 className="h-4 w-4" /> Quick fill</button>}
          <button type="button" onClick={downloadTemplate} className="btn-sp"><Download className="h-4 w-4" /> {ro ? 'Download' : 'CSV template'}</button>
          {!ro && <button type="button" onClick={() => fileRef.current?.click()} className="btn-sp"><Upload className="h-4 w-4" /> Import CSV</button>}
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importCsv(f); e.target.value = ''; }} />
        </div>
      </div>

      {ro && <p className="rounded-xl bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800">Targets are set by the Super Admin, Admin or Department Administrator. You can view them here.</p>}

      <Panel pad={false} title={`${m.label} — FY ${fy}`} subtitle={ro ? undefined : 'Type straight into the grid. Changed cells turn blue; nothing is saved until you press Save.'} icon={Target}>
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0 text-[13px]">
            <thead>
              <tr className="text-data-xs font-semibold uppercase tracking-wide text-slate-400">
                <th className="sticky left-0 z-10 bg-white" />
                {quarters.map((q) => <th key={q} colSpan={3} className="border-b border-slate-100 bg-slate-50/70 py-1.5 text-center">{q}</th>)}
                <th className="bg-white" />
              </tr>
              <tr className="text-left text-data-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="sticky left-0 z-10 min-w-[200px] border-b border-slate-200 bg-white px-4 py-2">Employee</th>
                {data.months.map((mo) => <th key={mo.key} className="border-b border-slate-200 px-1.5 py-2 text-center">{mo.label.split(' ')[0]}</th>)}
                <th className="border-b border-slate-200 px-4 py-2 text-right">FY total</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const total = data.months.reduce((n, mo) => n + Number(cellValue(r.id, mo.key, metric) || 0), 0);
                return (
                  <tr key={r.id} className="group">
                    <td className="sticky left-0 z-10 border-b border-slate-100 bg-white px-4 py-1.5 group-hover:bg-slate-50">
                      <p className="font-semibold text-slate-900">{r.name}</p>
                      <p className="text-data-xs text-slate-500">{[r.territory, r.managerName && `team of ${r.managerName}`].filter(Boolean).join(' · ') || r.department}</p>
                    </td>
                    {data.months.map((mo) => {
                      const v = cellValue(r.id, mo.key, metric);
                      const dirty = isDirty(r.id, mo.key, metric);
                      return (
                        <td key={mo.key} className="border-b border-slate-100 px-1 py-1 group-hover:bg-slate-50">
                          {ro ? <span className="block min-w-[84px] px-2 text-right tabular-nums text-slate-700">{v ? fmt(v) : '—'}</span> : (
                            <input type="number" min={0} value={v || ''} placeholder="0"
                              onChange={(e) => put(r.id, mo.key, metric, e.target.value === '' ? 0 : Math.max(0, Number(e.target.value)))}
                              className={`w-[92px] rounded-md border px-2 py-1 text-right text-[12px] tabular-nums outline-none focus:border-blue-500 ${dirty ? 'border-blue-300 bg-blue-50 font-semibold text-blue-800' : 'border-transparent bg-transparent hover:border-slate-200'}`} />
                          )}
                        </td>
                      );
                    })}
                    <td className="border-b border-slate-100 px-4 text-right font-semibold tabular-nums text-slate-900 group-hover:bg-slate-50">{fmt(total)}</td>
                  </tr>
                );
              })}
              <tr className="bg-slate-50 font-semibold">
                <td className="sticky left-0 z-10 bg-slate-50 px-4 py-2 text-slate-700">Team total</td>
                {data.months.map((mo) => <td key={mo.key} className="px-2 py-2 text-right text-[12px] tabular-nums text-slate-700">{colTotal(mo.key) ? fmt(colTotal(mo.key)) : '—'}</td>)}
                <td className="px-4 py-2 text-right tabular-nums text-slate-900">{fmt(data.months.reduce((n, mo) => n + colTotal(mo.key), 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>

      {!ro && changes.length > 0 && (
        <div className="sticky bottom-3 z-20 flex flex-wrap items-center gap-3 rounded-2xl border border-blue-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
          <span className="text-sm font-semibold text-slate-800">{changes.length} change{changes.length === 1 ? '' : 's'} not saved</span>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={() => setDraft({})} className="btn-sp"><X className="h-4 w-4" /> Discard</button>
            <button type="button" onClick={() => setSaving(true)} className="btn-sp-primary"><Save className="h-4 w-4" /> Save targets</button>
          </div>
        </div>
      )}

      <FillDialog open={fill} onClose={() => setFill(false)} rows={data.rows} months={data.months} metric={m}
        onApply={(ids, monthKeys, perMonth) => {
          setDraft((p) => {
            const n = { ...p };
            ids.forEach((u) => monthKeys.forEach((mk) => { n[keyOf(u, mk)] = { ...n[keyOf(u, mk)], [metric]: perMonth }; }));
            return n;
          });
          setFill(false);
        }} />

      <Dialog open={saving} onClose={() => setSaving(false)} title="Save targets" subtitle={`${changes.length} change(s) — they take effect on the dashboard straight away`}
        footer={(
          <>
            <button type="button" className="btn-sp" onClick={() => setSaving(false)}>Cancel</button>
            <button type="button" className="btn-sp-primary" disabled={!reason.trim() || save.isPending} onClick={async () => {
              try {
                const r = await save.mutateAsync({ rows: changes, reason }) as { changed: number };
                toast.success(`${r.changed} target(s) saved.`); setDraft({}); setReason(''); setSaving(false);
              } catch (err) { toast.error(errText(err, 'Could not save the targets.')); }
            }}>{save.isPending ? 'Saving…' : 'Save'}</button>
          </>
        )}>
        <Field label="Why are these targets being set or changed?" hint="Kept in the audit log with the old and new values.">
          <input className={inputCls} autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Q3 targets agreed in the sales review" />
        </Field>
      </Dialog>
    </div>
  );
}

function FillDialog({ open, onClose, rows, months, metric, onApply }: {
  open: boolean; onClose: () => void; rows: Array<{ id: string; name: string }>; months: Array<{ key: string; label: string; quarter: string }>;
  metric: (typeof METRICS)[number]; onApply: (ids: string[], months: string[], perMonth: number) => void;
}) {
  const [who, setWho] = useState<string[]>([]);
  const [span, setSpan] = useState('Q3');
  const [mode, setMode] = useState<'each' | 'split'>('each');
  const [amount, setAmount] = useState('');
  const monthKeys = span === 'year' ? months.map((m) => m.key) : span.startsWith('Q') ? months.filter((m) => m.quarter === span).map((m) => m.key) : [span];
  const n = Number(amount);
  const perMonth = mode === 'split' && monthKeys.length ? Math.round((n / monthKeys.length) * 100) / 100 : n;
  const ids = who.length ? who : rows.map((r) => r.id);
  return (
    <Dialog open={open} onClose={onClose} title={`Quick fill — ${metric.label}`} subtitle="Puts the same target into many cells at once. You can still edit any cell afterwards." width="max-w-xl"
      footer={(
        <>
          <button type="button" className="btn-sp" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-sp-primary" disabled={!(n >= 0) || amount === ''} onClick={() => onApply(ids, monthKeys, perMonth)}>Fill {ids.length * monthKeys.length} cells</button>
        </>
      )}>
      <div className="flex flex-col gap-4">
        <Field label="For">
          <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-slate-200 p-2">
            <button type="button" onClick={() => setWho([])} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${who.length === 0 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>Everyone</button>
            {rows.map((r) => (
              <button key={r.id} type="button" onClick={() => setWho((p) => (p.includes(r.id) ? p.filter((x) => x !== r.id) : [...p, r.id]))}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${who.includes(r.id) ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{r.name}</button>
            ))}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Period">
            <select className={inputCls} value={span} onChange={(e) => setSpan(e.target.value)}>
              <option value="year">Whole year (12 months)</option>
              {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => <option key={q} value={q}>{q} ({months.filter((m) => m.quarter === q).map((m) => m.label.split(' ')[0]).join(', ')})</option>)}
              {months.map((m) => <option key={m.key} value={m.key}>{m.label} only</option>)}
            </select>
          </Field>
          <Field label="The figure is">
            <select className={inputCls} value={mode} onChange={(e) => setMode(e.target.value as 'each' | 'split')} disabled={monthKeys.length === 1}>
              <option value="each">per month</option>
              <option value="split">a total — split equally across the months</option>
            </select>
          </Field>
        </div>
        <Field label={metric.label} hint={amount !== '' && monthKeys.length > 1 ? `Each month gets ${metric.money ? inr(perMonth) : perMonth}` : undefined}>
          <input className={inputCls} type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </Field>
      </div>
    </Dialog>
  );
}
